import type React from 'react';

import { IntakePreferenceSection } from '@/services/types/firms-types';

/**
 * Simple text highlighting for search matches.
 * Returns the text with matched portions wrapped in a yellow highlight.
 */
export const highlightText = (
  text: string,
  searchTerm: string,
): React.ReactNode => {
  if (!searchTerm.trim()) return text;

  const term = searchTerm.trim();
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;

  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + term.length);
  const after = text.slice(idx + term.length);

  return (
    <>
      {before}
      <mark className="bg-yellow-200 dark:bg-yellow-900/50 text-inherit rounded px-0.5">
        {match}
      </mark>
      {after}
    </>
  );
};

export interface SearchMatch {
  sectionIndex: number;
  sectionTitle: string;
  matchText: string;
  contextBefore: string;
  contextAfter: string;
  matchIndex: number;
  globalMatchIndex: number;
}

export interface SearchResults {
  query: string;
  matches: SearchMatch[];
  totalMatches: number;
  sectionsWithMatches: number;
}

export function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function extractSearchMatches(
  sections: IntakePreferenceSection[],
  query: string,
): SearchResults {
  if (!query.trim()) {
    return { query: '', matches: [], totalMatches: 0, sectionsWithMatches: 0 };
  }

  const regex = new RegExp(escapeRegex(query), 'gi');
  const matches: SearchMatch[] = [];
  let globalMatchIndex = 0;

  sections.forEach((section, sectionIndex) => {
    const { content } = section;
    const lines = content.split('\n');

    // Search in title
    const titleMatches = [...section.title.matchAll(regex)];
    globalMatchIndex += 1;

    titleMatches.forEach((match) => {
      matches.push({
        sectionIndex,
        sectionTitle: section.title,
        matchText: match[0],
        contextBefore: '',
        contextAfter: section.title, // Show the actual matched title
        matchIndex: match.index || 0,
        globalMatchIndex,
      });
    });

    // Search in content
    lines.forEach((line, lineIndex) => {
      const lineMatches = [...line.matchAll(regex)];

      lineMatches.forEach((match) => {
        // Get 2 lines before and after for context
        const contextBefore = lines
          .slice(Math.max(0, lineIndex - 2), lineIndex)
          .join('\n');
        globalMatchIndex += 1;

        matches.push({
          sectionIndex,
          sectionTitle: section.title,
          matchText: match[0],
          contextBefore,
          contextAfter: line, // Current line with match
          matchIndex: match.index || 0,
          globalMatchIndex,
        });
      });
    });
  });

  const sectionsWithMatches = new Set(matches.map((m) => m.sectionIndex)).size;

  return {
    query,
    matches,
    totalMatches: matches.length,
    sectionsWithMatches,
  };
}

export function highlightMatches(
  text: string,
  query: string,
  activeGlobalMatchIndex: number | null = null,
  allMatches: SearchMatch[] = [],
  currentSectionIndex: number | null = null,
): React.ReactNode {
  if (!query.trim()) return text;

  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
  const parts = text.split(regex);

  // Get all matches for the current section
  const sectionMatches = allMatches.filter(
    (m) => m.sectionIndex === currentSectionIndex,
  );

  let matchOccurrenceInSection = 0;

  return parts.map((part, index) => {
    const isMatch = regex.test(part);
    // Reset regex lastIndex after test
    regex.lastIndex = 0;

    if (isMatch) {
      // Get the global match index for this occurrence
      const currentMatch = sectionMatches[matchOccurrenceInSection];
      const isThisMatchActive =
        currentMatch &&
        currentMatch.globalMatchIndex === activeGlobalMatchIndex;

      const element = (
        <mark
          key={index}
          className={`font-medium px-0.5 rounded transition-colors ${
            isThisMatchActive
              ? 'bg-yellow-300 text-gray-900' // Active match - bright yellow like Chrome
              : 'bg-yellow-100 text-gray-900' // Other matches - light yellow
          }`}
          data-global-match-index={currentMatch?.globalMatchIndex}
          data-is-active={isThisMatchActive}
        >
          {part}
        </mark>
      );

      matchOccurrenceInSection += 1;
      return element;
    }

    return <span key={index}>{part}</span>;
  });
}
